import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeadingBackButton } from '@/components/ui/page-heading';
import { InstitutionOnboardingProgressStep, useProgress } from '@/context/progress';
import { InstitutionGoToReviewStepperButton } from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { type InstitutionFuturesReporting, institutionFuturesReportingSchema } from '@/features/institution/futures/reporting/schema';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsMutation, useInstitutionDetailsV2Answers, useInstitutionDetailsVersion } from '@/features/institution/hooks/useInstitutionDetails';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import type { FormStep } from '@/types/components';
import { InstitutionFuturesReportingFieldsetGeneral, InstitutionFuturesReportingFieldsetGeneralLegend } from './fieldsets/general';

const FORM_STEPS = {
  GENERAL: {
    Component: InstitutionFuturesReportingFieldsetGeneral,
    Legend: InstitutionFuturesReportingFieldsetGeneralLegend,
    schema: institutionFuturesReportingSchema,
  },
} satisfies Record<string, FormStep<InstitutionFuturesReporting>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionFuturesReportingOnboardingFormCurrentStep = () => {
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionFuturesReporting>();
  const { data: version } = useInstitutionDetailsVersion();
  const { setCurrentStep } = useProgress();
  const Step = STEPS[currentStep];

  useEffect(() => {
    if (!version) return;

    return setCurrentStep(
      InstitutionOnboardingProgressStep.INSTITUTION_INFORMATION,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.ONBOARDING, version, 'reporting', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps, version]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
InstitutionFuturesReportingOnboardingFormCurrentStep.displayName = 'InstitutionFuturesReportingOnboardingFormCurrentStep';

const InstitutionFuturesReportingOnboardingFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<InstitutionFuturesReporting>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
InstitutionFuturesReportingOnboardingFormCurrentStepLegend.displayName = 'InstitutionFuturesReportingOnboardingFormCurrentStepLegend';

const InstitutionFuturesReportingOnboardingFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<InstitutionFuturesReporting>();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();

  const handlePrevStep = useCallback(() => {
    if (currentStep === 0) {
      navigate(`${onboardingPath}/client-categorisation`, { replace: true });
    } else {
      prevStep();
    }
  }, [currentStep, navigate, prevStep, onboardingPath]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionFuturesReportingOnboardingFormBackButton.displayName = 'InstitutionFuturesReportingOnboardingFormBackButton';

const InstitutionFuturesReportingOnboardingFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, nextStep } = useGuardedFormStepper<InstitutionFuturesReporting>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionFuturesReportingOnboardingFormFooter.displayName = 'InstitutionFuturesReportingOnboardingFormFooter';

const InstitutionFuturesReportingOnboardingForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'reporting');
  const { isPending, mutateAsync } = useInstitutionDetailsMutation();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const onSubmit = async (data: InstitutionFuturesReporting) => {
    await mutateAsync(
      { reporting: await institutionFuturesReportingSchema.parseAsync(data) },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate(`${onboardingPath}/review`, { replace: true });
        },
      },
    );
  };

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.reporting ?? {};

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <InstitutionFuturesReportingOnboardingFormCurrentStepLegend />
        <div className="flex flex-row justify-between -md:justify-end">
          <InstitutionFuturesReportingOnboardingFormBackButton />
          <InstitutionGoToReviewStepperButton onSubmit={onSubmit} />
        </div>
        <PageCard size="lg" bordered fixedFooter>
          <InstitutionFuturesReportingOnboardingFormCurrentStep />
          <InstitutionFuturesReportingOnboardingFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
InstitutionFuturesReportingOnboardingForm.displayName = 'InstitutionFuturesReportingOnboardingForm';

export default InstitutionFuturesReportingOnboardingForm;
